import { useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { UUID } from "crypto";
import { queryClient } from "@/queryClient";
import { getActivityTimeline } from "@/modules/lead-page/api/lead-page.v2.api";
import { useActivityContext } from "./activity-context.hook";
import { ActivityType } from "@/modules/lead-page/dtos/index-v2";
import { queryCommonFlags } from "@/api/constants";

const scrollToActivity = (activityUid: string) => {
  let attempts = 0;
  const maxAttempts = 50; // Try for 5 seconds

  const checkAndScroll = () => {
    // Find the activity element by searching for id that ends with the uid
    const allActivityElements = document.querySelectorAll('[id^="activity-"]');
    let element: HTMLElement | null = null;

    for (const el of Array.from(allActivityElements)) {
      if (el.id.endsWith(activityUid)) {
        element = el as HTMLElement;
        break;
      }
    }

    if (element) {
      // Use smooth scroll for better UX, but handle immediate scroll if needed
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      // Double check after scroll animation
      setTimeout(() => {
        element?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }, 500);
    } else if (attempts < maxAttempts) {
      attempts++;
      setTimeout(checkAndScroll, 100);
    } else {
      console.warn(
        `Could not find activity element with uid ending in ${activityUid} after ${maxAttempts} attempts`,
      );
    }
  };

  // Start checking
  setTimeout(checkAndScroll, 200); // Delay to ensure DOM is ready
};

export const useFindInActivity = () => {
  const { lead_id } = useParams<{ lead_id: UUID | string }>();
  const {
    setHighlightedActivityId,
    filters,
    queryKey,
    setIsFindingActivity,
    setFindInActivityRoute,
  } = useActivityContext();

  // Track the last found activity to prevent redundant fetches
  const lastFetchedActivityRef = useRef<string | null>(null);

  const findInActivity = useCallback(
    async (ratingUId: string, createdAt: string | Date) => {
      if (!lead_id) return;

      // Get filter settings from context
      const { type, includeChildLead } = filters ?? {};

      // Clear highlighted activity first to allow re-highlighting the same activity
      setHighlightedActivityId?.(undefined);

      // Check if the activity already exists in the current cache
      const existingData = queryClient.getQueryData(queryKey || []);
      if (
        existingData &&
        typeof existingData === "object" &&
        "pages" in existingData
      ) {
        const allItems = (existingData.pages as any[]).flatMap(
          (page: any) => page?.data?.items || [],
        );
        const activityExists = allItems.some(
          (item: any) =>
            item.uid === ratingUId ||
            item.id === ratingUId ||
            `${item.id}-${item.uid}` === ratingUId ||
            `${item.id}` === ratingUId,
        );

        if (activityExists) {
          // Activity already in cache, just highlight it and scroll
          setFindInActivityRoute?.(true);
          setHighlightedActivityId?.(ratingUId);
          scrollToActivity(ratingUId);
          return;
        }
      }

      // Check if we've already fetched this activity in this session
      if (lastFetchedActivityRef.current === ratingUId) {
        // Already fetched, just highlight and scroll
        setFindInActivityRoute?.(true);
        setHighlightedActivityId?.(ratingUId);
        scrollToActivity(ratingUId);
        return;
      }

      // Activity not in cache, fetch it
      setIsFindingActivity?.(true);
      setFindInActivityRoute?.(true);
      try {
        const ratingDate = new Date(createdAt);

        // Fetch a page that includes the target activity
        // Use cursor slightly after the target to include it in desc results
        const cursor = new Date(ratingDate.getTime() + 1000).toISOString();

        const response = await getActivityTimeline(lead_id, {
          limit: 30, // Fetch 30 items to get context around the target
          type: type as ActivityType[],
          includeChildLead: includeChildLead || false,
          cursor,
          direction: "desc",
        });

        const items = response.data.items || [];

        // Set query data with REAL API values - allows normal pagination
        queryClient.setQueryData(queryKey || [], {
          pages: [
            {
              data: {
                items,
                hasNext: response.data.hasNext, // Keep real - allows scroll up if more newer data
                nextCursor: response.data.nextCursor,
                hasPrev: response.data.hasPrev, // Keep real - allows scroll down if more older data
                prevCursor: response.data.prevCursor,
                count: response.data.count,
                hasChild: response.data.hasChild,
              },
            },
          ],
          pageParams: [undefined],
          ...queryCommonFlags,
        });

        // Store that we've fetched this activity
        lastFetchedActivityRef.current = ratingUId;

        // Force React Query to recompute derived state without refetching
        await queryClient.invalidateQueries({
          queryKey,
          refetchType: "none",
        });

        // Small delay to ensure state has propagated before highlighting
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Set highlighted activity ID after data is loaded
        setHighlightedActivityId?.(ratingUId);

        // Scroll to the highlighted activity
        // The scrollToActivity function has its own delay to ensure DOM is ready
        scrollToActivity(ratingUId);
      } catch (error) {
        setHighlightedActivityId?.(undefined);
        lastFetchedActivityRef.current = null;
      } finally {
        // Clear loading state after data is loaded
        setIsFindingActivity?.(false);
      }
    },
    [
      lead_id,
      filters,
      setHighlightedActivityId,
      queryKey,
      setIsFindingActivity,
      setFindInActivityRoute,
    ],
  );

  return { findInActivity };
};
